import { useMutation, useQuery } from "@apollo/client";
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Alert, CircularProgress, MenuItem, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Select from "react-select";

import { useDifficultyEnums } from "@/hooks/useDifficultyEnums";
import { useSpecialistDropdown } from "@/hooks/useSpecialistDropDown";
import { Loader } from "@/pages/UserDetails/styles";
import { Option } from "@/types/utils";

import { Button } from "../../../components/atoms/Button";
import { TextInput } from "../../../components/atoms/TextInput";
import { convertActivationStatus } from "../../../transformers/account.transformer";
import {
  CognitiveDomain,
  Feature,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  User,
  VoiceMessage,
} from "../../../types/api";
import { transformDate } from "../../../utils/date-utils";
import { GET_ALL_ACTIVITIES } from "../../ActivityLibrary/queries";
import { cognitiveDomainsOptions, featureOptions } from "../optionsData";
import {
  GET_SINGLE_ACTIVITY,
  SET_VOICE_MESSAGE_ACTIVATION,
  UPDATE_VOICE_MESSAGE,
} from "../queries";
import {
  Error,
  GoogleLink,
  LongerSingleWrapperDiv,
  SideBySideSubTitleDiv,
  SideBySideWrapperDiv,
  SingleSubTitleDiv,
  SingleWrapperDiv,
  StatusDiv,
  SubHeaderDiv,
} from "../styles";
import {
  ActivityDetailFormProps,
  CognitiveDomainWithFeature,
  FormInputs,
  SetVoiceMessageActivationInputs,
  SingleActivityResponse,
  UpdateVoiceMessageInputs,
} from "../types";

export const ActivityDetailForm = ({
  allData,
  activityId,
  isOpen,
  editView,
  setEditView,
  setSingleActivityError,
  setActivityFilterStatus,
}: ActivityDetailFormProps) => {
  const [showError, setShowError] = useState<boolean>(false);
  const [rectitle, setrectitle] = useState<string>("");
  const [recorderError, setRecorderError] = useState<boolean>(false);
  const [recId, setRecId] = useState<number>(0);
  const [activated, setActivated] = useState<boolean>(false);
  const [defaultTitle, setDefaultTitle] = useState<string>("");
  const [defaultId, setDefaultId] = useState<number>(0);

  const { options: recorderOptions, loading: loadingRecorders } = useSpecialistDropdown();

  const { difficultyOptions, loading: loadingDifficulty } = useDifficultyEnums();

  const {
    control,
    register,
    handleSubmit,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isValid },
    setValue,
    getValues,
    clearErrors,
    watch,
  } = useForm<FormInputs>({
    mode: "onChange",
  });

  const watchedFeature = watch("feature");

  useEffect(() => {
    if (
      watchedFeature &&
      watchedFeature !== Feature.HealthyBrainActivity &&
      watchedFeature !== Feature.CompensatoryStrategies
    ) {
      setValue("difficulty", undefined);
    }
  }, [watchedFeature, setValue]);

  const {
    data: singleActivityData,
    error: singleActivityError,
    loading: singleActivityLoading,
  } = useQuery<SingleActivityResponse>(GET_SINGLE_ACTIVITY, {
    variables: { id: activityId },
    fetchPolicy: "cache-and-network",
    skip: !isOpen,
  });

  useEffect(() => {
    setSingleActivityError(singleActivityError);
  }, [singleActivityError]);

  const [updateVoiceMessage, { error: updateVoiceMessageError }] =
    useMutation<UpdateVoiceMessageInputs>(UPDATE_VOICE_MESSAGE);

  const [setVoiceMessageActivation, { error: setVoiceMessageActivationError }] =
    useMutation<SetVoiceMessageActivationInputs>(SET_VOICE_MESSAGE_ACTIVATION, {
      refetchQueries: [{ query: GET_ALL_ACTIVITIES }, "GetAllActivities"],
    });

  const voiceMessage: VoiceMessage | undefined = singleActivityData?.voiceMessage;
  const downloadUrl = voiceMessage?.audioFile?.downloadUrl;

  useEffect(() => {
    if (updateVoiceMessageError || setVoiceMessageActivationError) {
      setShowError(true);
    }
  }, [updateVoiceMessageError, setVoiceMessageActivationError]);

  useEffect(() => {
    if (singleActivityData) {
      setValue("title", voiceMessage?.title?.trim());
      setValue("templateLink", voiceMessage?.templateLink?.trim());
      setValue("recordedBy", voiceMessage?.recordedBy?.id);
      setValue("feature", voiceMessage?.feature);
      setValue("cognitiveDomains", voiceMessage?.cognitiveDomains ?? []);
      setValue("status", convertActivationStatus(voiceMessage?.deletedAt));
      setValue("difficulty", voiceMessage?.difficulty);
    }
    if (singleActivityData?.voiceMessage?.deletedAt === null) {
      setValue("deactivationNote", "");
    }
    voiceMessage?.recordedBy?.id
      ? (setRecId(voiceMessage?.recordedBy?.id), setDefaultId(voiceMessage?.recordedBy?.id))
      : null;
    voiceMessage?.title
      ? (setrectitle(voiceMessage?.title?.trim()), setDefaultTitle(voiceMessage?.title?.trim()))
      : null;
  }, [singleActivityData, isOpen, singleActivityData?.voiceMessage?.deletedAt]);

  const [filteredCogDomainOptions, setFilteredCogDomainOptions] = useState<
    CognitiveDomainWithFeature[]
  >([]);

  const updateCogDomainOptions = (feature: Feature | undefined) => {
    setFilteredCogDomainOptions(
      cognitiveDomainsOptions.filter(
        (cogDomain: CognitiveDomainWithFeature) => cogDomain.feature === feature
      )
    );
  };

  useEffect(() => {
    updateCogDomainOptions(voiceMessage?.feature);
  }, [voiceMessage?.feature]);

  const onSubmit: SubmitHandler<FormInputs> = async (response) => {
    try {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const variables: any = {
        id: activityId,
        recordedById: response.recordedBy || undefined,
        title: response?.title?.trim() || "",
        templateLink: response?.templateLink || "",
        feature: response.feature || undefined,
        cognitiveDomains: response.cognitiveDomains || undefined,
      };

      if (
        response.feature === Feature.HealthyBrainActivity ||
        response.feature === Feature.CompensatoryStrategies
      ) {
        if (response.difficulty) {
          variables.difficulty = response.difficulty.toUpperCase();
        } else {
          variables.difficulty = null;
        }
      } else {
        variables.difficulty = null;
      }
      await updateVoiceMessage({
        variables,
      });
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Failed to update voice message");
      setShowError(true);
    }
    await setVoiceMessageActivation({
      variables: {
        id: activityId,
        isActive: response.status === "Active",
        deactivationNote: response.deactivationNote,
      },
    });
    setEditView(false);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-empty
    try {
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Failed to set voice message activation");
      setShowError(true);
    }
  };

  const cognitiveDomains: string[] = voiceMessage?.cognitiveDomains ?? [];
  const joinedCognitiveDomains = cognitiveDomains.join(", ");

  const defaultCognitiveDomains = cognitiveDomains
    .map((singleCogDomain) =>
      cognitiveDomainsOptions.findIndex((cogDomain) => cogDomain.value === singleCogDomain)
    )
    .filter((index) => index >= 0)
    .map((index) => cognitiveDomainsOptions?.[index])
    .filter((x) => !!x);

  const featureBoolean = getValues("feature") === Feature.Instructions;

  const getRecorderDisplayName = (recorderId: number | undefined) => {
    if (recorderId === undefined || recorderId === null) {
      return "";
    }

    const recorder = recorderOptions.find((option) => option.value === recorderId);
    return recorder ? recorder.label : "";
  };

  useEffect(() => {
    const defaultTitleValue = defaultTitle.toLowerCase();

    const ActiveRecorder = allData?.find(
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: any) =>
        item?.title?.toLowerCase().trim() === rectitle?.toLowerCase() &&
        item?.recordedBy?.id === recId
    );

    if (defaultTitleValue !== rectitle || defaultId !== recId) {
      if (
        ActiveRecorder?.recordedBy?.id === recId &&
        ActiveRecorder?.title?.toLowerCase() === rectitle?.toLowerCase()
      ) {
        setRecorderError(true);
      } else {
        setRecorderError(false);
      }
    }
    if (defaultTitleValue === rectitle.toLowerCase()) {
      setRecorderError(false);
    }
  }, [recId, rectitle, activated, allData]);

  const SubmitButton = () => {
    return (
      <Button type="submit" disabled={recorderError}>
        Submit
      </Button>
    );
  };

  const urlRegex =
    /^(https?:\/\/(www\.)?(google\.com\/.*|docs\.google\.com\/.*|drive\.google\.com\/.*|([a-zA-Z0-9]+\.)*[a-zA-Z0-9]+\.[^\s]{2,})|google\.com\/.*|docs\.google\.com\/.*|drive\.google\.com\/.*)$/;

  return (
    <>
      {singleActivityLoading || loadingRecorders || loadingDifficulty ? (
        <Loader>
          <CircularProgress />
        </Loader>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <SingleWrapperDiv>
            <SubHeaderDiv>Audio file</SubHeaderDiv>
            <SingleSubTitleDiv>
              {downloadUrl ? (
                <audio src={downloadUrl} controls style={{ position: "relative", zIndex: 0 }} />
              ) : (
                "No audio file found"
              )}
            </SingleSubTitleDiv>
          </SingleWrapperDiv>
          <SingleWrapperDiv>
            <SubHeaderDiv>
              {!editView ? "Title" : "Title *"}
              <SingleSubTitleDiv>
                {!editView ? (
                  voiceMessage?.title
                ) : (
                  <div>
                    <TextInput
                      fullWidth
                      readonly={!editView}
                      {...register("title", {
                        required: "Title is required",
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onChange(e: any) {
                          setrectitle(e.target.value.toLowerCase().trim());
                        },
                      })}
                    />
                    <Error>{errors?.title?.message}</Error>
                  </div>
                )}
              </SingleSubTitleDiv>
            </SubHeaderDiv>
          </SingleWrapperDiv>
          <SingleWrapperDiv>
            <SubHeaderDiv>
              {!editView ? "Google Template Link" : "Google Template Link"}
              <SingleSubTitleDiv>
                {!editView ? (
                  <GoogleLink onClick={() => window.open(voiceMessage?.templateLink)}>
                    {voiceMessage?.templateLink ? voiceMessage?.templateLink : ""}
                  </GoogleLink>
                ) : (
                  <div>
                    <TextInput
                      fullWidth
                      readonly={!editView}
                      {...register("templateLink", {
                        pattern: {
                          value: urlRegex,
                          message: "Not a valid entry, please enter a Google doc link",
                        },
                      })}
                    />
                    <Error>{errors?.templateLink?.message}</Error>
                  </div>
                )}
              </SingleSubTitleDiv>
            </SubHeaderDiv>
          </SingleWrapperDiv>
          <SideBySideWrapperDiv>
            <SubHeaderDiv>
              {!editView ? "Recorder" : "Recorder *"}
              <SideBySideSubTitleDiv>
                {""}
                {!editView ? (
                  getRecorderDisplayName(voiceMessage?.recordedBy?.id)
                ) : (
                  <Controller
                    name="recordedBy"
                    control={control}
                    rules={{ required: "Recorder is required" }}
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <Select
                          options={recorderOptions}
                          isDisabled={!editView}
                          styles={{
                            menuPortal: (base) =>
                              // TODO: Fix this the next time the file is edited.
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              ({ ...base, zIndex: 0 } as any),
                            // TODO: Fix this the next time the file is edited.
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            menu: (base) => ({ ...base, zIndex: 0 } as any),
                          }}
                          menuPortalTarget={document.body}
                          value={
                            recorderOptions.find((option) => option.value === value) || {
                              label: "-",
                              value: -1,
                            }
                          }
                          defaultValue={
                            recorderOptions.find(
                              (option) => option.value === voiceMessage?.recordedBy?.id
                            ) || { label: "-", value: -1 }
                          }
                          onChange={(
                            // TODO: Fix this the next time the file is edited.
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            selectedOption: Partial<Option<any>> | null
                          ) => {
                            const newFeature = selectedOption?.value;
                            onChange(newFeature);
                            setValue("recordedBy", newFeature);
                            setRecId(newFeature);
                          }}
                        />
                      </div>
                    )}
                  />
                )}
              </SideBySideSubTitleDiv>
            </SubHeaderDiv>
            <SubHeaderDiv>
              Date Created
              <SideBySideSubTitleDiv>
                {transformDate(voiceMessage?.createdOn)}
              </SideBySideSubTitleDiv>
            </SubHeaderDiv>
          </SideBySideWrapperDiv>
          {editView ? (
            recorderError ? (
              <Error>
                A name already exists by that name for that recorder. Either choose a new, unique
                name for this activity or deactivate the other activity prior to saving this one.
              </Error>
            ) : (
              ""
            )
          ) : (
            ""
          )}

          {editView ? <Error>{errors?.recordedBy?.message}</Error> : ""}
          <SingleWrapperDiv>
            <SubHeaderDiv>
              {!editView ? "Message Type" : "Message Type *"}
              <SingleSubTitleDiv>
                {!editView ? (
                  voiceMessage?.feature
                ) : (
                  <Controller
                    name="feature"
                    control={control}
                    rules={{ required: "Feature is required" }}
                    render={({ field: { onChange } }) => (
                      <div>
                        <Select
                          defaultValue={{
                            value: voiceMessage?.feature,
                            label: voiceMessage?.feature?.toString(),
                          }}
                          options={featureOptions}
                          isDisabled={!editView}
                          styles={{
                            menuPortal: (base) =>
                              // TODO: Fix this the next time the file is edited.
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              ({ ...base, zIndex: 0 } as any),
                            // TODO: Fix this the next time the file is edited.
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            menu: (base) => ({ ...base, zIndex: 0 } as any),
                          }}
                          menuPortalTarget={document.body}
                          onChange={(selectedOption: Partial<Option<Feature>> | null) => {
                            clearErrors("cognitiveDomains");
                            const newFeature = selectedOption?.value;
                            setValue("cognitiveDomains", []);
                            updateCogDomainOptions(newFeature);
                            onChange(newFeature);
                          }}
                        />
                        <Error>{errors?.feature?.message}</Error>
                      </div>
                    )}
                  />
                )}
              </SingleSubTitleDiv>
            </SubHeaderDiv>
          </SingleWrapperDiv>
          {(watchedFeature === Feature.HealthyBrainActivity ||
            watchedFeature === Feature.CompensatoryStrategies) && (
            <SingleWrapperDiv>
              <SubHeaderDiv>Difficulty</SubHeaderDiv>
              <SingleSubTitleDiv>
                {!editView ? (
                  voiceMessage?.difficulty || "-"
                ) : (
                  <Controller
                    name="difficulty"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        options={difficultyOptions}
                        isDisabled={!editView}
                        styles={{
                          // TODO: Fix this the next time the file is edited.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          menuPortal: (base) => ({ ...base, zIndex: 0 } as any),
                          // TODO: Fix this the next time the file is edited.
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          menu: (base) => ({ ...base, zIndex: 0 } as any),
                        }}
                        menuPortalTarget={document.body}
                        value={difficultyOptions.find((option) => option.value === value) || null}
                        onChange={(selectedOption: Partial<Option<string>> | null) => {
                          onChange(selectedOption?.value || null);
                        }}
                        isClearable
                      />
                    )}
                  />
                )}
              </SingleSubTitleDiv>
            </SingleWrapperDiv>
          )}
          <SingleWrapperDiv>
            <SubHeaderDiv>
              {featureBoolean ? "Cognitive Domain *" : "Cognitive Domain"}
              <SingleSubTitleDiv>
                {!editView ? (
                  joinedCognitiveDomains
                ) : (
                  <Controller
                    name="cognitiveDomains"
                    control={control}
                    rules={{
                      validate: {
                        requiredForFeature: (value: CognitiveDomain[] | []) =>
                          featureBoolean ? value.length > 0 : true,
                      },
                    }}
                    render={({ field: { onChange, value } }) => {
                      const joinedValue = value?.join(",");
                      return (
                        <div>
                          <Select
                            key={`cognitive-domains=${joinedValue}`}
                            isMulti
                            defaultValue={defaultCognitiveDomains}
                            options={filteredCogDomainOptions}
                            isDisabled={!editView}
                            styles={{
                              menuPortal: (base) =>
                                // TODO: Fix this the next time the file is edited.
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                ({ ...base, zIndex: 0 } as any),
                              // TODO: Fix this the next time the file is edited.
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              menu: (base) => ({ ...base, zIndex: 0 } as any),
                            }}
                            menuPortalTarget={document.body}
                            value={
                              value
                                ?.map((cogDomain: CognitiveDomain) =>
                                  cognitiveDomainsOptions.find(
                                    (option) => option.value === cogDomain
                                  )
                                )
                                // TODO: Fix this the next time the file is edited.
                                // eslint-disable-next-line no-extra-boolean-cast
                                ?.flatMap((x) => (!!x ? [x] : [])) ?? []
                            }
                            onChange={(
                              selectedOptions: readonly CognitiveDomainWithFeature[] | null
                            ) => {
                              if (selectedOptions?.[0]?.feature === Feature.Instructions) {
                                const selectedInstruction = selectedOptions?.slice(-1)?.[0]?.value;
                                onChange(
                                  // TODO: Fix this the next time the file is edited.
                                  // eslint-disable-next-line no-extra-boolean-cast
                                  !!selectedInstruction ? [selectedInstruction] : []
                                );
                                return;
                              }

                              const optionsArr: string[] =
                                selectedOptions
                                  ?.map((option) => option?.value)
                                  ?.filter((x) => !!x) ?? [];
                              onChange(optionsArr);
                            }}
                          />
                          <Error>
                            {errors?.cognitiveDomains &&
                              "Activities with feature `Instructions` must have a cognitive domain selected."}
                          </Error>
                        </div>
                      );
                    }}
                  />
                )}
              </SingleSubTitleDiv>
            </SubHeaderDiv>
          </SingleWrapperDiv>

          <LongerSingleWrapperDiv>
            <SubHeaderDiv>
              {!editView ? "Status" : "Status *"}
              {!editView ? (
                <SingleSubTitleDiv>
                  <StatusDiv>{convertActivationStatus(voiceMessage?.deletedAt)}</StatusDiv>
                </SingleSubTitleDiv>
              ) : (
                <div>
                  <input
                    {...register("status", { required: "Status is required" })}
                    type="radio"
                    value="Active"
                    id="rdactive"
                    name="statusradio"
                    onChange={() => {
                      setActivityFilterStatus("Active");
                      setValue("status", "Active");
                      setActivated(true);
                    }}
                  />
                  Active
                  <input
                    {...register("status", { required: "Status is required" })}
                    type="radio"
                    value="Deactivated"
                    id="rddeactivated"
                    name="statusradio"
                    onChange={() => {
                      setValue("status", "Deactivated");
                      setActivated(false);
                    }}
                  />
                  Deactivated
                  <SingleWrapperDiv>
                    <TextInput
                      fullWidth
                      placeholder="(Optional) Reason for deactivation"
                      {...register("deactivationNote")}
                    />
                  </SingleWrapperDiv>
                  <p>{errors?.status?.message}</p>
                </div>
              )}
            </SubHeaderDiv>
          </LongerSingleWrapperDiv>
          <br />
          <SingleWrapperDiv> {editView && <SubmitButton />}</SingleWrapperDiv>
        </form>
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to update voice message.
        </Alert>
      </Snackbar>
    </>
  );
};
